import { DocRole, type GetPageGrantedUsersListQuery } from '@affine/graphql';
import {
  catchErrorInto,
  effect,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  Service,
} from '@toeverything/infra';
import { EMPTY, exhaustMap, tap } from 'rxjs';

import type { DocService } from '../../doc';
import type { WorkspaceService } from '../../workspace';
import type { DocGrantedUsersStore } from '../stores/doc-granted-users';
import type { ObjectPermissionMutationService } from './object-permission-mutation';

export type GrantedUser = NonNullable<
  GetPageGrantedUsersListQuery['workspace']['doc']
>['grantedUsersList']['edges'][number]['node'];

export class DocGrantedUsersService extends Service {
  constructor(
    private readonly store: DocGrantedUsersStore,
    private readonly workspaceService: WorkspaceService,
    private readonly docService: DocService,
    private readonly objectPermissionMutation: ObjectPermissionMutationService
  ) {
    super();
  }

  fetchReviewPage(
    pagination: { first: number; after?: string },
    options: {
      search?: string;
      includeCandidates?: boolean;
      guestsOnly?: boolean;
    },
    signal: AbortSignal
  ) {
    return this.store.fetchDocGrantedUsersList(
      this.workspaceService.workspace.id,
      this.docService.doc.id,
      pagination,
      signal,
      options
    );
  }

  readonly PAGE_SIZE = 8;

  nextCursor$ = new LiveData<string | undefined>(undefined);
  hasMore$ = new LiveData(true);
  grantedUserCount$ = new LiveData(0);
  grantedUsers$ = new LiveData<GrantedUser[]>([]);
  isLoading$ = new LiveData(false);
  error$ = new LiveData<any>(null);

  readonly loadMore = effect(
    exhaustMap(() => {
      if (!this.hasMore$.value) {
        return EMPTY;
      }
      return fromPromise(async signal => {
        return await this.store.fetchDocGrantedUsersList(
          this.workspaceService.workspace.id,
          this.docService.doc.id,
          {
            first: this.PAGE_SIZE,
            after: this.nextCursor$.value,
          },
          signal
        );
      }).pipe(
        tap(({ edges, pageInfo, totalCount }) => {
          this.grantedUsers$.next([
            ...this.grantedUsers$.value,
            ...edges.map(edge => edge.node),
          ]);

          this.grantedUserCount$.next(totalCount);
          this.hasMore$.next(pageInfo.hasNextPage);
          this.nextCursor$.next(pageInfo.endCursor ?? undefined);
        }),
        catchErrorInto(this.error$),
        onStart(() => {
          this.isLoading$.setValue(true);
        }),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  reset() {
    this.grantedUsers$.setValue([]);
    this.grantedUserCount$.setValue(0);
    this.hasMore$.setValue(true);
    this.nextCursor$.setValue(undefined);
    this.isLoading$.setValue(false);
    this.error$.setValue(null);
    this.loadMore.reset();
  }

  async grantUsersRole(userIds: string[], role: DocRole) {
    await Promise.all(
      userIds.map(userId =>
        this.objectPermissionMutation.execute({
          action: 'grant',
          principal: { id: userId, kind: 'user' },
          role,
          target: { id: this.docService.doc.id, kind: 'doc' },
        })
      )
    );
    this.grantedUsers$.next(
      this.grantedUsers$.value.map(user => {
        if (userIds.includes(user.user.id)) {
          return { ...user, role };
        }
        return user;
      })
    );
  }

  async revokeUsersRole(userId: string) {
    await this.objectPermissionMutation.execute({
      action: 'revoke',
      principal: { id: userId, kind: 'user' },
      target: { id: this.docService.doc.id, kind: 'doc' },
    });
    this.grantedUsers$.next(
      this.grantedUsers$.value.filter(user => user.user.id !== userId)
    );
    if (this.grantedUserCount$.value > 0) {
      this.grantedUserCount$.next(this.grantedUserCount$.value - 1);
    }
  }

  async updateUserRole(userId: string, role: DocRole) {
    await this.objectPermissionMutation.execute({
      action: 'update',
      principal: { id: userId, kind: 'user' },
      role,
      target: { id: this.docService.doc.id, kind: 'doc' },
    });
    if (role === DocRole.Owner) {
      this.reset();
      this.loadMore();
      return true;
    }
    this.grantedUsers$.next(
      this.grantedUsers$.value.map(user => {
        if (user.user.id === userId) {
          return { ...user, role };
        }
        return user;
      })
    );

    return true;
  }

  async updateDocDefaultRole(
    role: DocRole,
    options?: { inheritFromFolder?: boolean }
  ) {
    return await this.store.updateDocDefaultRole({
      docId: this.docService.doc.id,
      workspaceId: this.workspaceService.workspace.id,
      inheritFromFolder: options?.inheritFromFolder === true,
      role,
    });
  }

  override dispose(): void {
    this.loadMore.unsubscribe();
  }
}
